import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckCircle2, XCircle, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function FinalExam() {
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [locked, setLocked] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/exam").then(r => setQuestions(r.data)).catch(e => {
      if (e?.response?.status === 402) setLocked(true);
    });
  }, []);

  const submit = async () => {
    if (Object.keys(answers).length !== questions.length) {
      toast.error("Answer every question first."); return;
    }
    setSubmitting(true);
    try {
      const arr = questions.map((_, i) => answers[i]);
      const r = await api.post("/exam/submit", { answers: arr });
      setResult(r.data);
      if (r.data.passed) toast.success(`Passed with ${r.data.score}%! Certificate unlocked.`);
      else toast.error(`Scored ${r.data.score}%. You need 80% to pass.`);
    } catch (e) {
      toast.error("Submit failed");
    } finally { setSubmitting(false); }
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto p-6 lg:p-12">
        {locked ? (
          <div className="border border-accent rounded-md p-10 bg-accent/5 text-center" data-testid="exam-paywall">
            <div className="label-caps mb-2 text-accent">Final exam locked</div>
            <h1 className="font-serif text-4xl lg:text-5xl mb-4">Enroll to earn your certificate.</h1>
            <p className="text-muted-foreground mb-8 max-w-md mx-auto">The final exam and downloadable certificate are included with any plan.</p>
            <Button asChild size="lg" data-testid="exam-see-plans">
              <a href="/pricing">See plans</a>
            </Button>
          </div>
        ) : (
          <>
        <div className="label-caps mb-3">Final assessment</div>
        <h1 className="font-serif text-4xl lg:text-6xl leading-none">Final Exam</h1>
        <p className="mt-4 text-muted-foreground">25 questions drawn from all 13 chapters. Score 80% or higher to earn your certificate.</p>

        <div className="mt-10 space-y-4">
          {questions.map((q, i) => (
            <Card key={i} className="p-5" data-testid={`exam-q-${i}`}>
              <div className="label-caps mb-2">Q{i + 1}</div>
              <div className="font-medium mb-3">{q.q}</div>
              <div className="grid gap-2">
                {q.options.map((opt, oi) => {
                  const selected = answers[i] === oi;
                  const correctIdx = result?.correct_answers?.[i];
                  const isCorrect = result && oi === correctIdx;
                  const isWrong = result && selected && !result.results[i];
                  return (
                    <button
                      key={oi}
                      type="button"
                      data-testid={`exam-${i}-option-${oi}`}
                      onClick={() => !result && setAnswers(a => ({ ...a, [i]: oi }))}
                      disabled={!!result}
                      className={`text-left px-4 py-2 rounded-md border text-sm ${
                        isCorrect ? "border-primary bg-primary/10" :
                        isWrong ? "border-destructive bg-destructive/10" :
                        selected ? "border-primary bg-secondary" : "border-border hover:border-primary/50"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-xs font-mono text-muted-foreground">{String.fromCharCode(65 + oi)}</span>
                        <span className="flex-1">{opt}</span>
                        {isCorrect && <CheckCircle2 size={14} className="text-primary" />}
                        {isWrong && <XCircle size={14} className="text-destructive" />}
                      </div>
                    </button>
                  );
                })}
              </div>
            </Card>
          ))}
        </div>

        {!result ? (
          <Button className="mt-8" size="lg" onClick={submit} disabled={submitting} data-testid="exam-submit-btn">
            {submitting ? "Grading…" : "Submit Final Exam"}
          </Button>
        ) : (
          <Card className="p-6 mt-8" data-testid="exam-result">
            <div className="label-caps mb-2">Final Score</div>
            <div className="font-serif text-5xl">{result.score}%</div>
            <div className="text-sm text-muted-foreground mt-1">{result.correct} of {result.total} · {result.passed ? "Passed" : "Not passed (80% required)"}</div>
            <div className="flex gap-3 mt-6">
              {result.passed && <Button onClick={() => nav("/certificate")} data-testid="go-cert-btn">View Certificate <ArrowRight size={14} className="ml-2" /></Button>}
              <Button variant="outline" onClick={() => { setResult(null); setAnswers({}); }}>Retake</Button>
            </div>
          </Card>
        )}
        </>
        )}
      </div>
    </Layout>
  );
}
